import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  LinearProgress,
  Paper,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  School,
  TrendingUp,
  Assignment,
  Quiz,
  EmojiEvents,
  Schedule,
  CheckCircle,
  AccessTime,
  Star,
  Book,
  Psychology,
  Science,
  Calculate,
  Language,
  History,
  Brush,
  MusicNote,
  Timer,
  CalendarToday,
  PlayArrow,
  Done,
  RadioButtonUnchecked,
} from '@mui/icons-material';
import { courseDetailService, type CourseDetail, type CourseTopic, type CourseActivity } from '../services/courseDetailService';

const CourseDetailView: React.FC = () => {
  const { childId, courseId } = useParams<{ childId: string; courseId: string }>();
  const navigate = useNavigate();
  const [courseDetail, setCourseDetail] = useState<CourseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const loadCourseDetail = async () => {
      if (!childId || !courseId) {
        setError('Child ID and Course ID are required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await courseDetailService.getCourseDetail(parseInt(childId), parseInt(courseId));
        setCourseDetail(data);
      } catch (err: any) {
        console.error('Error loading course detail:', err);
        setError(err.message || 'Failed to load course details');
      } finally {
        setLoading(false);
      }
    };

    loadCourseDetail();
  }, [childId, courseId]);

  const handleBack = () => {
    navigate(`/parent-child-dashboard/${childId}`);
  };

  const getSubjectIcon = (subject: string) => {
    switch (subject.toLowerCase()) {
      case 'mathematics':
        return <Calculate color="primary" />;
      case 'english':
        return <Language color="secondary" />;
      case 'science':
        return <Science color="success" />;
      case 'physics':
        return <Science color="success" />;
      case 'chemistry':
        return <Science color="success" />;
      case 'biology':
        return <Science color="success" />;
      case 'history':
        return <History color="info" />;
      case 'art':
        return <Brush color="warning" />;
      case 'music':
        return <MusicNote color="error" />;
      default:
        return <Book />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'in_progress':
        return <PlayArrow color="primary" />;
      default:
        return <RadioButtonUnchecked color="disabled" />;
    }
  };

  const getActivityIcon = (type: CourseActivity['type']) => {
    switch (type) {
      case 'topic_completed':
        return <CheckCircle color="success" />;
      case 'topic_started':
        return <PlayArrow color="primary" />;
      case 'quiz_completed':
        return <Quiz color="success" />;
      case 'quiz_attempted':
        return <Quiz color="info" />;
      case 'assignment_submitted':
        return <Assignment color="primary" />;
      case 'assignment_graded':
        return <Assignment color="success" />;
      default:
        return <School />;
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.toLocaleDateString()} at ${date.toLocaleTimeString()}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  if (!courseDetail) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Course details not found.</Alert>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  const { topics, recentActivities, statistics } = courseDetail;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Paper elevation={1} sx={{ bgcolor: 'white', borderBottom: 1, borderColor: 'divider' }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={handleBack}>
                <ArrowBack />
              </IconButton>
              {getSubjectIcon(courseDetail.subject)}
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {courseDetail.courseName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {courseDetail.subject} • {courseDetail.difficulty}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Key Metrics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <TrendingUp sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                  {Math.round(courseDetail.overallProgress)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Overall Progress
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Star sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'warning.main' }}>
                  {courseDetail.averageScore !== null ? `${Math.round(courseDetail.averageScore)}%` : 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average Score
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <AccessTime sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {formatTime(courseDetail.totalStudyTime)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Study Time
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Book sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'info.main' }}>
                  {statistics.completedTopics}/{statistics.totalTopics}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Topics Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Course Description */}
        {courseDetail.courseDescription && (
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Course Description
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {courseDetail.courseDescription}
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Statistics Summary */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Learning Statistics
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Topics: {statistics.totalTopics}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {statistics.completedTopics} completed, {statistics.inProgressTopics} in progress, {statistics.notStartedTopics} not started
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Quizzes: {statistics.totalQuizzes}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {statistics.completedQuizzes} completed
                      {statistics.averageQuizScore !== null && ` (Avg: ${Math.round(statistics.averageQuizScore)}%)`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Assignments: {statistics.totalAssignments}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {statistics.submittedAssignments} submitted, {statistics.gradedAssignments} graded
                      {statistics.averageAssignmentScore !== null && ` (Avg: ${Math.round(statistics.averageAssignmentScore)}%)`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Study Sessions: {statistics.totalStudySessions}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total time: {formatTime(statistics.totalStudyTimeMinutes)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Enrollment Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Enrolled:</Typography>
                    <Typography variant="body1">{formatDate(courseDetail.enrolledAt)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Last Accessed:</Typography>
                    <Typography variant="body1">{formatDate(courseDetail.lastAccessed)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Status:</Typography>
                    <Chip 
                      label={courseDetail.enrollmentStatus} 
                      color={courseDetail.enrollmentStatus === 'ENROLLED' || courseDetail.enrollmentStatus === 'IN_PROGRESS' ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  {courseDetail.currentTopic && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Current Topic:</Typography>
                      <Typography variant="body1">{courseDetail.currentTopic}</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Content Tabs */}
        <Paper sx={{ mb: 4 }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="Topics" />
            <Tab label="Recent Activity" />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Grid container spacing={2}>
            {topics.length === 0 ? (
              <Grid item xs={12}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 6 }}>
                    <Book sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      No Topics Available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Topics will appear here once they are added to the course.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ) : (
              topics.map((topic) => (
                <Grid item xs={12} key={topic.topicId}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        {getStatusIcon(topic.status)}
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {topic.topicTitle}
                          </Typography>
                          {topic.topicDescription && (
                            <Typography variant="body2" color="text.secondary">
                              {topic.topicDescription}
                            </Typography>
                          )}
                        </Box>
                        <Chip 
                          label={topic.status.replace('_', ' ')} 
                          color={
                            topic.status === 'completed' ? 'success' :
                            topic.status === 'in_progress' ? 'primary' : 'default'
                          }
                          size="small"
                        />
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Progress
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {Math.round(topic.progress)}%
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={topic.progress} 
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {topic.estimatedTimeMinutes && (
                          <Chip 
                            icon={<AccessTime />}
                            label={`${topic.estimatedTimeMinutes} min`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {topic.hasQuiz && (
                          <Chip 
                            icon={<Quiz />}
                            label={topic.quizScore !== null ? `Quiz: ${Math.round(topic.quizScore)}%` : 'Quiz Available'}
                            size="small"
                            color={topic.quizScore !== null ? 'success' : 'default'}
                            variant="outlined"
                          />
                        )}
                        {topic.hasAssignment && (
                          <Chip 
                            icon={<Assignment />}
                            label={topic.assignmentSubmitted ? 'Assignment Submitted' : 'Assignment Pending'}
                            size="small"
                            color={topic.assignmentSubmitted ? 'success' : 'warning'}
                            variant="outlined"
                          />
                        )}
                        {topic.completedAt && (
                          <Chip 
                            icon={<Done />}
                            label={`Completed ${formatDate(topic.completedAt)}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
        )}

        {activeTab === 1 && (
          <Card>
            <CardContent>
              {recentActivities.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Schedule sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    No Recent Activity
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Activity will appear here as your child engages with the course.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {recentActivities.map((activity, index) => (
                    <React.Fragment key={activity.id}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'background.paper' }}>
                            {getActivityIcon(activity.type)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {activity.title}
                              </Typography>
                              {activity.score !== null && (
                                <Chip 
                                  label={`${Math.round(activity.score)}%`} 
                                  size="small" 
                                  color={activity.status === 'success' ? 'success' : 'error'}
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box component="span">
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'block' }} component="span">
                                {activity.description}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} component="span">
                                <Typography variant="caption" color="text.secondary" component="span">
                                  {activity.topicTitle}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" component="span">
                                  {formatDateTime(activity.timestamp)}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < recentActivities.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
};

export default CourseDetailView;

